import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, Globe, Inbox, KeyRound, Newspaper, Plus, RefreshCw, Settings2 } from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { Card, CardBody, CardHeader } from '@/components/card';
import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/page-header';
import { SkeletonList } from '@/components/skeleton';
import { StatusPill } from '@/components/status-dot';
import { Tabs } from '@/components/tabs';
import { SecretRevealDialog } from '@/components/secret-reveal';
import { Dialog } from '@/components/dialog';
import { DataList, DataListRow } from '@/components/data-list';
import { useCreateForm, useSiteForms } from '@/lib/forms';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  useRegeneratePreviewToken,
  useRegenerateSiteKey,
  useSite,
  useUpdateSite,
  type SiteInput,
} from '@/lib/sites';
import { useFormError } from '@/lib/form-error';
import { formatDate } from '@/lib/format';
import type { ClientFormData, SiteData } from '@/types/api';

const STATUS_TONE = {
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
} as const;

export function SiteDetail() {
  const { id } = useParams();
  const siteId = Number(id);
  const site = useSite(Number.isFinite(siteId) ? siteId : undefined);

  if (site.isLoading) {
    return <SkeletonList rows={6} />;
  }

  if (!site.data) {
    return (
      <EmptyState
        icon={Globe}
        title="Site not found"
        description="It may have been deleted, or the link is wrong."
        action={
          <Link to="/sites">
            <Button variant="secondary">Back to websites</Button>
          </Link>
        }
      />
    );
  }

  const data = site.data;

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Globe}
        title={data.name}
        subtitle={data.domain ?? undefined}
        badge={
          <StatusPill tone={STATUS_TONE[data.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
            {data.status}
          </StatusPill>
        }
        action={
          data.domain ? (
            <a href={`https://${data.domain}`} target="_blank" rel="noreferrer">
              <Button variant="secondary">
                Visit site <ExternalLink className="size-4" />
              </Button>
            </a>
          ) : null
        }
      />

      {data.client_name ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">
          Owned by{' '}
          <Link to={`/clients/${data.client_id}`} className="text-primary hover:underline">
            {data.client_name}
          </Link>
        </p>
      ) : null}

      <Tabs
        paramName="tab"
        tabs={[
          { key: 'overview', label: 'Overview', icon: Settings2, content: <OverviewTab site={data} /> },
          { key: 'forms', label: 'Forms', icon: Inbox, content: <FormsTab site={data} /> },
          { key: 'keys', label: 'Keys', icon: KeyRound, content: <KeysTab site={data} /> },
          { key: 'posts', label: 'Blog', icon: Newspaper, content: <PostsTab site={data} /> },
        ]}
      />
    </div>
  );
}

function OverviewTab({ site }: { site: SiteData }) {
  const update = useUpdateSite(site.id);
  const { error, wrap } = useFormError();
  const toast = useToast();
  const [pending, setPending] = useState(false);

  const [form, setForm] = useState<SiteInput>({
    name: site.name,
    domain: site.domain ?? '',
    staging_url: site.staging_url ?? '',
    status: site.status,
    brand_voice: site.brand_voice ?? '',
  });

  // Re-seed when the query refetches a newer server copy.
  useEffect(() => {
    setForm({
      name: site.name,
      domain: site.domain ?? '',
      staging_url: site.staging_url ?? '',
      status: site.status,
      brand_voice: site.brand_voice ?? '',
    });
  }, [site]);

  function set<K extends keyof SiteInput>(key: K, value: SiteInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    const ok = await wrap(() =>
      update.mutateAsync({
        ...form,
        domain: form.domain || null,
        staging_url: form.staging_url || null,
        brand_voice: form.brand_voice || null,
      }),
    );
    setPending(false);
    if (ok) toast.success('Site updated');
  }

  return (
    <form onSubmit={onSubmit} className="max-w-2xl space-y-4">
      <Card>
        <CardHeader>
          <h3 className="text-sm font-semibold">Details</h3>
        </CardHeader>
        <CardBody className="space-y-4">
          <label className="block space-y-1.5 text-sm">
            <span className="font-medium">Name</span>
            <Input value={form.name ?? ''} onChange={(e) => set('name', e.target.value)} required />
          </label>

          <label className="block space-y-1.5 text-sm">
            <span className="font-medium">Domain</span>
            <Input
              value={form.domain ?? ''}
              onChange={(e) => set('domain', e.target.value)}
              placeholder="example.com"
            />
          </label>

          <label className="block space-y-1.5 text-sm">
            <span className="font-medium">Staging URL</span>
            <Input
              value={form.staging_url ?? ''}
              onChange={(e) => set('staging_url', e.target.value)}
              placeholder="https://staging.example.com"
            />
          </label>

          <label className="block space-y-1.5 text-sm">
            <span className="font-medium">Status</span>
            <Select value={form.status ?? 'active'} onChange={(e) => set('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </Select>
            <span className="block text-xs text-[var(--color-foreground-muted)]">
              A paused site stops serving blog content and stops accepting form submissions.
            </span>
          </label>

          <label className="block space-y-1.5 text-sm">
            <span className="font-medium">Brand voice</span>
            <Textarea
              value={form.brand_voice ?? ''}
              onChange={(e) => set('brand_voice', e.target.value)}
              placeholder="Tone and style notes shown to whoever writes posts for this site."
            />
          </label>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <div className="flex justify-end">
            <Button type="submit" disabled={pending}>
              {pending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </form>
  );
}

function KeysTab({ site }: { site: SiteData }) {
  return (
    <div className="max-w-2xl space-y-4">
      <SecretCard
        title="Site key"
        description="Sent as the X-Site-Key header by the client's Astro build to read blog content. Read-only access."
        issued={site.has_api_key}
        site={site}
        kind="api"
      />
      <SecretCard
        title="Preview token"
        description="Lets a staging build read submitted-but-unpublished posts. Lower value than the site key, so it is stored in plaintext."
        issued
        site={site}
        kind="preview"
      />
    </div>
  );
}

function SecretCard({
  title,
  description,
  issued,
  site,
  kind,
}: {
  title: string;
  description: string;
  issued: boolean;
  site: SiteData;
  kind: 'api' | 'preview';
}) {
  const regenerateKey = useRegenerateSiteKey(site.id);
  const regenerateToken = useRegeneratePreviewToken(site.id);
  const confirm = useConfirm();
  const toast = useToast();
  const [revealed, setRevealed] = useState<string | null>(null);

  const pending = kind === 'api' ? regenerateKey.isPending : regenerateToken.isPending;

  async function rotate() {
    if (issued) {
      const ok = await confirm({
        title: `Generate a new ${title.toLowerCase()}?`,
        body: 'The current one stops working immediately. Anything using it will break until you update it.',
        confirmLabel: 'Generate new',
        tone: 'danger',
      });
      if (!ok) return;
    }

    if (kind === 'api') {
      const { api_key } = await regenerateKey.mutateAsync();
      setRevealed(api_key);
    } else {
      const { preview_token } = await regenerateToken.mutateAsync();
      setRevealed(preview_token);
    }
    toast.success(`New ${title.toLowerCase()} generated`);
  }

  return (
    <Card>
      <CardHeader className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        {issued ? <StatusPill tone="success">Issued</StatusPill> : <StatusPill tone="neutral">Not set</StatusPill>}
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-sm text-[var(--color-foreground-muted)]">{description}</p>
        <Button variant="secondary" onClick={rotate} disabled={pending}>
          <RefreshCw className="size-4" />
          {pending ? 'Generating…' : issued ? 'Generate new' : 'Generate'}
        </Button>
      </CardBody>

      {revealed ? (
        <SecretRevealDialog
          open
          onClose={() => setRevealed(null)}
          title={`New ${title.toLowerCase()}`}
          label={title}
          secret={revealed}
          description={`Give this to whoever configures ${site.name}.`}
        />
      ) : null}
    </Card>
  );
}

function PostsTab({ site }: { site: SiteData }) {
  const navigate = useNavigate();

  return (
    <Card>
      <CardBody className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">
            {site.posts_count ?? 0} {site.posts_count === 1 ? 'post' : 'posts'}
          </p>
          <p className="text-sm text-[var(--color-foreground-muted)]">
            {site.last_deployed_at
              ? `Last deployed ${formatDate(site.last_deployed_at)}`
              : 'Not deployed from here yet.'}
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate(`/blog?site=${site.id}`)}>
          <Newspaper className="size-4" /> Open blog
        </Button>
      </CardBody>
    </Card>
  );
}

function FormsTab({ site }: { site: SiteData }) {
  const forms = useSiteForms(site.id);
  const [createOpen, setCreateOpen] = useState(false);
  const list = forms.data?.data ?? [];

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-[var(--color-foreground-muted)]">
          Each form on this site posts its submissions to its own endpoint, with its own secret.
        </p>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" /> New form
        </Button>
      </div>

      {forms.isLoading ? (
        <SkeletonList rows={3} />
      ) : list.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No forms yet"
          description="Add one, generate its secret, and hand the snippet to whoever maintains the site."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> New form
            </Button>
          }
        />
      ) : (
        <DataList>
          {list.map((form) => (
            <DataListRow key={form.id} interactive={false}>
              <div className="min-w-0 flex-1">
                <Link
                  to={`/sites/${site.id}/forms/${form.id}`}
                  className="font-medium hover:underline"
                >
                  {form.name}
                </Link>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-mono">{form.slug}</span>
                  <span aria-hidden>·</span>
                  <span>{form.fields.length} fields</span>
                  <span aria-hidden>·</span>
                  <span>{form.submissions_count ?? 0} submissions</span>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {!form.has_secret ? <StatusPill tone="warning">No secret</StatusPill> : null}
                {form.unmapped_keys.length > 0 ? (
                  <StatusPill tone="info">{form.unmapped_keys.length} new</StatusPill>
                ) : null}
                <StatusPill tone={STATUS_TONE[form.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
                  {form.status}
                </StatusPill>
              </div>
            </DataListRow>
          ))}
        </DataList>
      )}

      <NewFormDialog siteId={site.id} open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

function NewFormDialog({
  siteId,
  open,
  onClose,
}: {
  siteId: number;
  open: boolean;
  onClose: () => void;
}) {
  const create = useCreateForm(siteId);
  const { error, wrap } = useFormError();
  const toast = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    let created: ClientFormData | undefined;
    const ok = await wrap(async () => {
      created = await create.mutateAsync({ name, slug });
    });
    setPending(false);
    if (ok && created) {
      toast.success(`"${created.name}" created`, 'Generate its secret from the Setup tab.');
      setName('');
      setSlug('');
      onClose();
      navigate(`/sites/${siteId}/forms/${created.id}?tab=setup`);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="New form" size="sm">
      <form onSubmit={onSubmit} className="space-y-4">
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">Name</span>
          <Input
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slug) {
                setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
              }
            }}
            placeholder="Contact form"
            required
          />
        </label>
        <label className="block space-y-1.5 text-sm">
          <span className="font-medium">Slug</span>
          <Input
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="contact"
            className="font-mono"
            required
          />
          <span className="block text-xs text-[var(--color-foreground-muted)]">
            Lowercase letters, numbers and dashes. The client dev can use this as their config key.
          </span>
        </label>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!name || !slug || pending}>
            {pending ? 'Creating…' : 'Create form'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
